// src/oura/components/docs/write/NameLinesView.tsx
//
// Oura's own edits (B16), a view inside the doc drawer: the file name, and
// the 2 lines Oura keeps at the top of the doc (line 1 the title, line 2 the
// version). Each shows as it stands and as it will read, so a person sees
// the change before clicking. Oura never rewrites a line an open comment
// sits on: that line waits until the comment is resolved, and says whose
// comment holds it. Nothing else in the doc's body is touched.

import { useState } from "react";
import { CheckIcon, MessageSquareIcon } from "lucide-react";
import { renameDocument, setDocLines, type DocLinesIn } from "@service/aeo/clientDocsWrite";
import type { BusinessDocuments, DocThread, DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { DrawerBlock, DrawerFoot, PillButton, SURFACE, TYPE, type DrawerFootMessage } from "@oura/ui";
import { docsFailureText, trimText } from "@oura/docs";
import { decidedName } from "../boardModel";
import { ouraLines, type DocWriteView } from "./model";

/** "Now" over "In the format", or one line with a tick when they already agree. */
function BeforeAfter({ label, now, want, held }: { label: string; now: string | null; want: string; held?: DocThread | null }) {
    const same = now === want;
    return (
        <div className={cn("border-t py-3 first:border-t-0 first:pt-1", SURFACE.hairline)}>
            <p className={cn(TYPE.lab, "m-0")}>{label}</p>
            {same ? (
                <p className={cn(TYPE.tc, "m-0 mt-1 flex items-start gap-1.5 break-words text-gray-900")}>
                    <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span className="min-w-0 break-words font-mono">{want}</span>
                </p>
            ) : (
                <div className={cn(SURFACE.inner, "mt-1.5 space-y-1.5")}>
                    <p className={cn(TYPE.tc, "m-0 break-words text-gray-500")}>
                        <span className="font-medium">Now </span>
                        <span className="font-mono line-through decoration-gray-300">{now ?? "Not read yet"}</span>
                    </p>
                    <p className={cn(TYPE.tc, "m-0 break-words text-gray-900")}>
                        <span className="font-medium text-gray-500">After </span>
                        <span className="font-mono">{want}</span>
                    </p>
                </div>
            )}
            {!same && held && (
                <p className={cn(TYPE.tc, "m-0 mt-1.5 flex items-start gap-1.5 text-amber-800")}>
                    <MessageSquareIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-amber-600" />
                    <span className="min-w-0 break-words">
                        {held.openedBy.name}&rsquo;s open comment sits on this line, so Oura leaves it until the comment is resolved.
                        <span className="mt-0.5 block text-gray-600">&ldquo;{trimText(held.text, 100)}&rdquo;</span>
                    </span>
                </p>
            )}
        </div>
    );
}

export function NameLinesView({
    doc,
    detail,
    businessName,
    now,
    plan = null,
    onBusy,
    onView,
}: {
    doc: StaffDocument;
    detail: DocumentDetail | null;
    businessName: string;
    now: number;
    /** The board's signed plan, for a monthly doc's "Month N" (ourteam-backend#6868). */
    plan?: BusinessDocuments["plan"];
    onBusy: (busy: boolean) => void;
    onView: (view: DocWriteView | "doc") => void;
}) {
    const [busy, setBusy] = useState<"rename" | "lines" | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const decided = decidedName(doc, businessName, now, plan);
    const lines = ouraLines(doc, detail, businessName, now, plan);

    if (!decided || !lines) {
        return (
            <>
                <DrawerBlock>
                    <p className="m-0 text-gray-700">Pick a code first. The name and the title line are written from the doc&rsquo;s codes.</p>
                </DrawerBlock>
                <DrawerFoot second={<PillButton onClick={() => onView("doc")}>Back to the doc</PillButton>} />
            </>
        );
    }

    const nameOff = !decided.nameMatches;
    const titleOff = lines.title !== lines.wantTitle;
    const versionOff = lines.version !== lines.wantVersion;
    // Only the lines no open comment holds.
    const patch: DocLinesIn = {
        ...(titleOff && !lines.titleHeld ? { titleLine: lines.wantTitle } : {}),
        ...(versionOff && !lines.versionHeld ? { versionLine: lines.wantVersion } : {}),
    };
    const canLines = Object.keys(patch).length > 0;
    const held = (titleOff && lines.titleHeld) || (versionOff && lines.versionHeld);

    const run = async (what: "rename" | "lines") => {
        setBusy(what);
        onBusy(true);
        const r = what === "rename" ? await renameDocument(doc.id, { name: decided.name }) : await setDocLines(doc.id, patch);
        setBusy(null);
        onBusy(false);
        if (!r.ok) {
            setMessage({ tone: "error", text: docsFailureText(r, "the document") });
            return;
        }
        setMessage({ tone: "success", text: what === "rename" ? "Renamed to the format." : held ? "Updated the lines no comment holds." : "Updated Oura's lines." });
    };

    const renameButton = (primary: boolean) => (
        <PillButton variant={primary ? "primary" : "secondary"} busy={busy === "rename"} disabled={busy !== null} onClick={() => void run("rename")}>
            Rename to the format
        </PillButton>
    );
    const linesButton = (primary: boolean) => (
        <PillButton variant={primary ? "primary" : "secondary"} busy={busy === "lines"} disabled={busy !== null} onClick={() => void run("lines")}>
            Update the lines
        </PillButton>
    );
    const back = (
        <PillButton disabled={busy !== null} onClick={() => onView("doc")}>
            Back to the doc
        </PillButton>
    );
    const first = nameOff ? renameButton(true) : canLines ? linesButton(true) : undefined;
    const second = nameOff && canLines ? linesButton(false) : back;

    return (
        <>
            <DrawerBlock label="File name" busy={busy === "rename"}>
                <BeforeAfter label="In Drive" now={doc.name.replace(/\.(docx?|pdf|xlsx?)$/i, "").trim()} want={decided.name} />
            </DrawerBlock>
            <DrawerBlock label="Lines Oura keeps" busy={busy === "lines"}>
                <BeforeAfter label="Line 1, the title" now={lines.title} want={lines.wantTitle} held={lines.titleHeld} />
                {/* A doc with no version (#4874) keeps line 2 empty: shown only when it holds something to clear or keep. */}
                {(lines.wantVersion || lines.version) && (
                    <BeforeAfter label={lines.wantVersion ? "Line 2, the version" : "Line 2, left empty"} now={lines.version} want={lines.wantVersion} held={lines.versionHeld} />
                )}
                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>Oura keeps these lines and its numbered question list.</p>
            </DrawerBlock>
            <DrawerFoot message={message} first={first} second={second} />
        </>
    );
}
